import { toJpeg } from 'html-to-image'

export interface CaptureResult {
  /** Base64-encoded JPEG image data (no data: prefix) */
  base64: string
  /** Width of the captured image in pixels */
  width: number
  /** Height of the captured image in pixels */
  height: number
}

/** Max dimension for the longest edge of captured images */
const MAX_SIZE = 1280

/**
 * Captures a DOM element as a JPEG screenshot using html-to-image.
 * No permissions required — uses the browser's native SVG foreignObject
 * rendering to capture the DOM exactly as rendered.
 */
function resolveTarget(target?: HTMLElement | string): HTMLElement {
  return typeof target === 'string'
    ? (document.querySelector<HTMLElement>(target) ?? document.body)
    : (target ?? document.body)
}

function scaledSize(element: HTMLElement): { width: number; height: number; pixelRatio: number } {
  const srcWidth = element.scrollWidth
  const srcHeight = element.scrollHeight

  // Calculate scale to fit within MAX_SIZE
  let width = srcWidth
  let height = srcHeight
  let pixelRatio = 1

  if (width > MAX_SIZE || height > MAX_SIZE) {
    pixelRatio = MAX_SIZE / Math.max(width, height)
    width = Math.round(width * pixelRatio)
    height = Math.round(height * pixelRatio)
  }
  return { width, height, pixelRatio }
}

/**
 * The dimensions a capture of `target` WOULD have, without capturing. The
 * panel-layout prompt uses this coordinate space on turns that send no image.
 */
export function measureCapture(target?: HTMLElement | string): { width: number; height: number } {
  const { width, height } = scaledSize(resolveTarget(target))
  return { width, height }
}

/**
 * Expensive: clones the whole DOM and copies every computed style on the main
 * thread (~7 s on a 15k-node dashboard, 2026-10-01). Call only when the turn
 * will actually send the image.
 */
export async function captureScreen(target?: HTMLElement | string): Promise<CaptureResult> {
  const element = resolveTarget(target)
  const { width, height, pixelRatio } = scaledSize(element)

  const dataUrl = await toJpeg(element, {
    quality: 0.8,
    pixelRatio,
    cacheBust: true,
    skipFonts: true, // Skip cross-origin font stylesheets
    filter: (node: HTMLElement) => {
      if (node.tagName === 'IFRAME') return false
      return true
    },
  })

  const base64 = dataUrl.split(',')[1]
  return { base64, width, height }
}
